import { motion, AnimatePresence } from 'framer-motion';
import SmartLink from './SmartLink';
import { HiOutlineX, HiOutlineMinus, HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi';
import { useCartStock } from '../hooks/useCartStock';
import { formatPrice, calculateSubtotal } from '../utils/formatPrice';
import StockNotice from './StockNotice';

const CartDrawer = () => {
  const {
    items,
    isDrawerOpen,
    toggleDrawer,
    removeFromCart,
    increaseQuantity,
    decreaseQuantity,
    getStock,
    getCartStockIssue,
    canProceedToCheckout,
    notice,
  } = useCartStock();

  const subtotal = calculateSubtotal(items);
  const cartStockIssue = getCartStockIssue();

  return (
    <AnimatePresence>
      {isDrawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70]"
            onClick={() => toggleDrawer(false)}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-lumora-dark-surface border-l border-lumora-cream/10 z-[80] shadow-hero flex flex-col"
          >
            <div className="flex items-center justify-between p-6 border-b border-lumora-cream/10">
              <h2 className="font-serif text-2xl text-lumora-cream">Your Cart</h2>
              <button
                onClick={() => toggleDrawer(false)}
                className="p-2 rounded-full hover:bg-lumora-cream/10 transition-colors text-lumora-cream"
                aria-label="Close cart"
              >
                <HiOutlineX className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <StockNotice notice={notice} className="mb-4" />
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-16 px-4">
                  <p className="text-lumora-muted mb-6 text-center">Your cart is empty</p>
                  <SmartLink to="/shop" onClick={() => toggleDrawer(false)} className="btn-primary">
                    Explore Collection
                  </SmartLink>
                </div>
              ) : (
                <div className="space-y-6">
                  {items.map((item) => {
                    const stock = getStock(item.id);
                    const atMax = item.quantity >= stock;
                    return (
                      <motion.div
                        key={item.id}
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex gap-4 p-4 rounded-2xl bg-lumora-dark-card/80 border border-lumora-cream/10"
                      >
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-20 h-20 rounded-xl object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-serif text-sm text-lumora-cream truncate">{item.title}</h3>
                          <p className="text-lumora-gold font-medium mt-1">{formatPrice(item.price)}</p>
                          {stock <= 0 && (
                            <p className="text-xs text-red-400 mt-1 font-medium">Sold out</p>
                          )}
                          <div className="flex items-center gap-3 mt-2">
                            <button
                              type="button"
                              onClick={() => decreaseQuantity(item)}
                              className="w-7 h-7 rounded-full border border-lumora-cream/15 flex items-center justify-center hover:bg-lumora-cream/10 transition-colors text-lumora-cream"
                            >
                              <HiOutlineMinus className="w-3 h-3" />
                            </button>
                            <span className="text-sm w-6 text-center text-lumora-cream">{item.quantity}</span>
                            <button
                              type="button"
                              onClick={() => increaseQuantity(item)}
                              disabled={atMax}
                              className="w-7 h-7 rounded-full border border-lumora-cream/15 flex items-center justify-center hover:bg-lumora-cream/10 transition-colors text-lumora-cream disabled:opacity-40"
                            >
                              <HiOutlinePlus className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeFromCart(item.id)}
                              className="ml-auto p-1 text-lumora-muted hover:text-red-400 transition-colors"
                            >
                              <HiOutlineTrash className="w-4 h-4" />
                            </button>
                          </div>
                          {atMax && stock > 0 && (
                            <p className="text-xs text-amber-400 mt-1">Only {stock} in stock</p>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="p-6 border-t border-lumora-cream/10 space-y-4 btn-row-mobile">
                <div className="flex justify-between text-sm w-full">
                  <span className="text-lumora-muted">Subtotal</span>
                  <span className="font-medium text-lumora-gold">{formatPrice(subtotal)}</span>
                </div>
                {cartStockIssue && (
                  <p className="text-red-400 text-xs text-center leading-relaxed">{cartStockIssue}</p>
                )}
                <SmartLink
                  to="/cart"
                  onClick={() => toggleDrawer(false)}
                  className="btn-secondary text-center"
                >
                  View Cart
                </SmartLink>
                {canProceedToCheckout ? (
                  <SmartLink
                    to="/checkout"
                    onClick={() => toggleDrawer(false)}
                    className="btn-primary text-center"
                  >
                    Checkout
                  </SmartLink>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="btn-primary opacity-50 cursor-not-allowed"
                  >
                    Checkout
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
